/**
 * Connection & lifecycle message handlers — extracted from MultiplayerManager.
 *
 * Handles: room_code, room_ready, hash_match, hash_mismatch, resync_request,
 * resync_snapshot, opponent_disconnect, opponent_forfeit, opponent_rematch, error
 */

import { lookupWeapon } from '../weapons';
import { getAllTankIds, lookupTank, setShowBonusTanks, getVisibleTankIds } from '../tanks';
import { createModeState } from '../game/mode';
import { serializeState, deserializeState } from './sync';
import { applyEffects, type Renderers } from '../game/effects';
import type { GameState } from '../types';
import type { GameClient } from './client';
import type { NetworkInput } from '../input';
import type { NetDebugLog } from './debug-log';

export interface ConnectionHandlerDeps {
    readonly client: GameClient;
    readonly networkInput: NetworkInput;
    readonly debugLog: NetDebugLog | null;
    getState(): GameState;
    getRenderers(): Renderers | null;
    getPendingRoomSeed(): number;
    setPendingRoomSeed(seed: number): void;
    clearDisconnectGrace(): void;
    getDisconnectGraceTimer(): ReturnType<typeof setTimeout> | null;
    setDisconnectGraceTimer(timer: ReturnType<typeof setTimeout> | null): void;
}

export function registerConnectionHandlers(deps: ConnectionHandlerDeps): void {
    const { client } = deps;

    client.on('room_code', (msg) => {
        const state = deps.getState();
        if (import.meta.env.DEV) console.log('[NET] Received room_code:', msg.code, 'multiplayer state:', !!state.multiplayer);
        if (state.multiplayer) {
            state.multiplayer.roomCode = msg.code;
            state.multiplayer.lobbyStatus = 'Waiting for opponent...';
        }
    });

    client.on('room_ready', (msg) => {
        const state = deps.getState();
        const mp = state.multiplayer;
        if (mp) {
            mp.enabled = true;
            mp.mySlot = msg.yourSlot;
            mp.opponentName = msg.opponentName;
            mp.connected = true;
            mp.opponentReady = false;
            mp.lobbyStatus = '';

            // Toggle stage select chooser on rematch (roundNumber > 0 means a game has been played)
            if (state.roundNumber > 0) {
                mp.stageSelectChooser = mp.stageSelectChooser === 0 ? 1 : 0;
            }
        }

        // Store seed for later (used after both players confirm tank select)
        deps.setPendingRoomSeed(msg.seed);

        // Initialize mode state
        state.modeState = createModeState('vs_online');

        // Reset stage select state
        state.stageSelectReady = false;

        // Enter tank select (bonus tanks hidden by default)
        setShowBonusTanks(false);
        state.tankSelectReady = [false, false];
        state.tankSelectCursor = [0, 0];
        const tankIds = getVisibleTankIds(true);
        for (const p of state.players) {
            p.tankId = tankIds[0];
            p.color = lookupTank(p.tankId).visual.bodyColor;
        }
        state.phase = 'tank_select';
    });

    // Hash validation
    client.on('hash_match', (msg) => {
        if (deps.debugLog) {
            deps.debugLog.hashMatchCount++;
            deps.debugLog.addEvent('hash_match', msg.checkpointId ?? 0, 'Sync OK', 'ok');
        }
    });

    client.on('hash_mismatch', (msg) => {
        console.warn(`[NET] State hash mismatch at checkpoint ${msg.checkpointId}!`);
        if (deps.debugLog) {
            deps.debugLog.hashMismatchCount++;
            deps.debugLog.addEvent('hash_mismatch', msg.checkpointId ?? 0, 'DESYNC DETECTED', 'error');
            deps.debugLog.capturePreDesyncState(msg.checkpointId ?? 0);
        }
    });

    // Resync: server asks P1 to send snapshot
    client.on('resync_request', () => {
        const state = deps.getState();
        if (state.multiplayer?.mySlot === 0) {
            if (import.meta.env.DEV) console.log('[NET] Resync requested — sending snapshot');
            deps.debugLog?.addEvent('resync_request', state.frameCount, 'Sending snapshot', 'warn');
            const snapshot = serializeState(state);
            client.send({
                type: 'resync_snapshot',
                state: snapshot,
            });
        }
    });

    // Resync: P2 receives snapshot and applies it
    client.on('resync_snapshot', (msg) => {
        const state = deps.getState();
        if (import.meta.env.DEV) console.log('[NET] Applying resync snapshot');
        const weaponLookup = (id: string) => lookupWeapon(id);
        const partial = deserializeState(msg.state, weaponLookup);

        // Apply all game-critical fields from snapshot
        // (terrain grid was already restored inside deserializeState)
        for (const [key, value] of Object.entries(partial)) {
            if (key === 'players') {
                // Merge player fields individually to preserve object references
                const players = value as any[];
                for (let i = 0; i < players.length && i < state.players.length; i++) {
                    Object.assign(state.players[i], players[i]);
                }
            } else if (key === 'multiplayer') {
                // Don't overwrite local multiplayer config (mySlot, connected, etc.)
                continue;
            } else {
                (state as any)[key] = value;
            }
        }
        // Rebuild terrain visuals to match restored grid data
        const renderers = deps.getRenderers();
        if (renderers) {
            applyEffects([{ type: 'terrain_resync' }], renderers);
        }
        if (import.meta.env.DEV) console.log('[NET] Resync complete — terrain + state restored');
        if (deps.debugLog) {
            deps.debugLog.resyncCount++;
            deps.debugLog.addEvent('resync_applied', state.frameCount, 'State restored', 'warn');
            deps.debugLog.capturePostResyncState(state);
            // Log desync diff to console for debugging
            const diff = deps.debugLog.getLatestDesyncDiff();
            if (diff && diff.diffs.length > 0) {
                console.warn('[NET] Desync diff:', diff.diffs.map(d => `${d.field}: ${d.local} → ${d.remote}`).join(' | '));
            }
        }
    });

    // Opponent connection lost — server notifies immediately, starts grace timer
    client.on('opponent_connection_lost', (msg) => {
        const state = deps.getState();
        const mp = state.multiplayer;
        if (!mp?.enabled) return;
        if (import.meta.env.DEV) console.log(`[NET] opponent_connection_lost — ${msg.graceMs}ms grace`);
        deps.debugLog?.addEvent('disconnect', state.frameCount, 'Opponent connection lost', 'error');
        mp.disconnectPhase = 'reconnecting';
        mp.disconnectCountdownEnd = Date.now() + msg.graceMs;
        mp.disconnectCursor = 0;
    });

    // Opponent reconnected within grace period
    client.on('opponent_reconnected', () => {
        const state = deps.getState();
        const mp = state.multiplayer;
        if (!mp) return;
        if (import.meta.env.DEV) console.log('[NET] opponent_reconnected — dismissing overlay');
        mp.disconnectPhase = null;
        mp.disconnectCountdownEnd = 0;
        deps.clearDisconnectGrace();
    });

    // Disconnection finalized — grace expired on server
    client.on('opponent_disconnect', () => {
        const state = deps.getState();
        if (!state.multiplayer) return;
        if (import.meta.env.DEV) console.log('[NET] opponent_disconnect — finalizing');
        deps.debugLog?.addEvent('disconnect', state.frameCount, 'Opponent disconnected', 'error');
        if (state.multiplayer.disconnectPhase === 'reconnecting') {
            state.multiplayer.disconnectPhase = 'gone';
        } else {
            state.multiplayer.disconnectPhase = 'gone';
        }
        state.multiplayer.connected = false;
    });

    client.on('opponent_forfeit', () => {
        const state = deps.getState();
        if (state.multiplayer) {
            state.multiplayer.lobbyStatus = 'Opponent forfeited';
        }
    });

    client.on('opponent_rematch', () => {
        const state = deps.getState();
        if (state.multiplayer) {
            state.multiplayer.opponentReady = true;
        }
    });

    client.on('error', (msg) => {
        const state = deps.getState();
        console.error('[NET] Server error:', msg.message);
        if (state.multiplayer) {
            state.multiplayer.lobbyStatus = msg.message;
        }
    });
}
